<template>
  <aside class="document-page">
    <DocumentPageTitle title="Diagnostics" />

    <section class="document-section">
      <p v-if="loading">
        Loading...
      </p>

      <div
        v-else
        class="flex flex-col gap-8 p-4 "
      >
        <div
          v-for="section in visibleSections"
          :key="section.storeKey"
        >
          <h2 class="text-xl font-bold mb-4 text-gray-800 ">
            {{ section.label }}
          </h2>

          <div class="grid grid-cols-1 gap-6">
            <div
              v-for="(pageId, index) in section.pageIds"
              :key="`${section.storeKey}-${index}`"
              class="w-full"
            >
              <div
                v-show="isReportLoaded(section.storeKey, index)"
                :id="getContainerId(section.storeKey, index)"
                class="powerbi-container w-full !h-[400px] border border-gray-200 bg-white"
              />

              <div
                v-if="!isReportLoaded(section.storeKey, index)"
                class="relative w-full h-[400px] overflow-hidden border-2 border-gray-200 flex items-center justify-center bg-gray-50 rounded-sm"
              >
                <img
                  :src="placeholderPowerbiReport"
                  class="w-full h-full object-cover opacity-50"
                  alt="Report Placeholder"
                >

                <div class="absolute inset-0 backdrop-blur-[2px] z-1" />

                <AppButton
                  class="btn btn-solid-secondary absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 font-semibold"
                  @click="loadSpecificReport(section, pageId, index)"
                >
                  Load this report
                </AppButton>
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="visibleSections.length === 0"
          class="text-center p-10 text-gray-500"
        >
          No reports configured for display.
        </div>
      </div>
    </section>
  </aside>
</template>

<script setup>
import { HttpStatusCode } from 'axios';
import * as pbi from 'powerbi-client';
import { computed, nextTick, onUnmounted, ref } from 'vue';

import { AppButton } from '@/components/buttons';

import placeholderPowerbiReport from '@/assets/images/icons/placeholder-powerbi-report.webp';
import { useHttp } from '@/composables';
import { useAuthStore } from '@/store';
import { DocumentPageTitle } from '@/components/plan-document/index.js';
import { AppContentLoader } from '@/components/app/index.js';

const authStore = useAuthStore();

const loading = ref(true);
const settingsDictionary = ref({});
const token = ref(null);
let powerbiService = null;

// Track which specific reports (by key-index) have been loaded
const loadedReportsSet = ref(new Set());

const reportSections = [
  { label: 'People Development', pageName: 'powerbi-pageids-people-development', storeKey: 'peopleDevelopment' },
  { label: 'Economic Positioning', pageName: 'powerbi-pageids-economic-positioning', storeKey: 'economicPositioning' },
  { label: 'Integrated Services Provisioning', pageName: 'powerbi-pageids-integrated-services-provisioning', storeKey: 'integratedServicesProvisioning' },
  { label: 'Governance & Finance Management', pageName: 'powerbi-pageids-governance-and-financial-management', storeKey: 'GovernanceAndFinanceManagement' },
  { label: 'Data Sources', pageName: 'powerbi-pageids-data-sources', storeKey: 'dataSource' },
];

const visibleSections = computed(() => {
  return reportSections.map(section => {
    const rawValue = settingsDictionary.value[section.pageName];
    if (!rawValue) return null;

    // Split string by comma and trim whitespace
    const pageIds = rawValue.split(',').map(id => id.trim()).filter(id => id);

    return {
      ...section,
      pageIds,
    };
  }).filter(section => section && section.pageIds.length > 0);
});

const getSettingValue = (key) => {
  if (!settingsDictionary.value[key]) return null;
  return settingsDictionary.value[key].split(',')[0].trim();
};

const getContainerId = (storeKey, index) => {
  return `${storeKey}-container-${index}`;
};

const isReportLoaded = (storeKey, index) => {
  return loadedReportsSet.value.has(getContainerId(storeKey, index));
};

const loadSettingsAndToken = async () => {
  loading.value = true;

  try {
    const [settingsResponse, tokenResponse] = await Promise.all([
      useHttp().get('/util/settings'),
      useHttp().get('/profile/token'),
    ]);

    if (settingsResponse.statusCode === HttpStatusCode.Ok) {
      settingsDictionary.value = settingsResponse.data.items.reduce((acc, item) => {
        acc[item.key] = item.values;
        return acc;
      }, {});
    }

    if (tokenResponse) {
      token.value = tokenResponse.data;
    }

  } catch (error) {
    console.error('Failed to initialize dashboard data', error);
  } finally {
    loading.value = false;
  }
};

const loadSpecificReport = async (section, pageId, index) => {
  if (!token.value) {
    console.error('No token available to load report');
    return;
  }

  const containerId = getContainerId(section.storeKey, index);

  loadedReportsSet.value.add(containerId);

  await nextTick();

  // Initialize PowerBI Service
  if (!powerbiService) {
    powerbiService = new pbi.service.Service(
      pbi.factories.hpmFactory,
      pbi.factories.wpmpFactory,
      pbi.factories.routerFactory,
    );
  }

  const container = document.getElementById(containerId);
  if (container) {
    embedPowerBiReport(container, pageId);
  }
};

const embedPowerBiReport = (container, pageName) => {
  const reportId = getSettingValue('powerbi-report-id') || token.value.reportId;
  const lookupColumn = getSettingValue('powerbi-region-lookup-selector') || 'District';
  const filterValue = authStore.currentDistrict?.powerBiRegionLookupValue;

  if (!reportId) {
    console.error('Report ID missing');
    return;
  }

  const reportLevelFilter = {
    $schema: 'http://powerbi.com/product/schema#basic',
    target: { table: 'Lookup', column: lookupColumn },
    operator: 'In',
    values: [filterValue],
  };

  const embedConfig = {
    type: 'report',
    id: reportId,
    embedUrl: token.value.embedUrl,
    accessToken: token.value.embedToken,
    tokenType: pbi.models.TokenType.Embed,
    pageName: pageName,
    settings: {
      layoutType: pbi.models.LayoutType.Custom,
      customLayout: {
        displayOption: pbi.models.DisplayOption.FitToWidth,
      },
      panes: {
        filters: { expanded: false, visible: false },
        pageNavigation: { visible: false },
      },
      background: pbi.models.BackgroundType.Transparent,
    },
    filters: filterValue ? [reportLevelFilter] : [],
  };

  try {
    powerbiService.reset(container);
    powerbiService.embed(container, embedConfig);
  } catch (e) {
    console.error(`Failed to embed report in ${container.id}`, e);
  }
};

loadSettingsAndToken();

onUnmounted(() => {
  if (powerbiService) {
    // Reset all loaded containers
    loadedReportsSet.value.forEach(containerId => {
      const container = document.getElementById(containerId);
      if (container) powerbiService.reset(container);
    });
  }
});

</script>

<style scoped>
.powerbi-container {
  min-height: 400px;
  width: 100%;
  display: block;
}
</style>

